import React, { useState } from 'react';

import Grid from '@mui/material/Grid';
import { type Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import GridContainer from '@fd/ui/Layout/GridContainer';
import { Modal } from '@fd/ui/Modal/Modal';
import { ModalActions } from '@fd/ui/Modal/ModalActions';
import ModalButtons from '@fd/ui/Modal/ModalButtons';
import { ModalContent } from '@fd/ui/Modal/ModalContent';

const useStyles = makeStyles((theme: Theme) => ({
  textFields: {
    paddingBottom: theme.spacing(5),
  },
  subTitle: {
    paddingBottom: theme.spacing(2),
  },
  buttons: {
    paddingTop: theme.spacing(3),
    justifyContent: 'flex-end',
  },
  gridItem: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: { padding: theme.spacing(1) },
  },
}));

type CreateLocationModalProps = {
  onClose: () => void;
  onConfirm: (numberOfLocations: number, startingNumber?: number, tablePrefix?: string) => void;
  isTablePrefixEnabled: boolean;
  translate: TranslateFunction;
};

const CreateLocationModal = (props: CreateLocationModalProps) => {
  const { onClose, onConfirm, isTablePrefixEnabled, translate } = props;
  const [numberOfLocations, setNumberOfLocations] = useState<number>(0);
  const [startingNumber, setStartingNumber] = useState<number>(1);
  const [tablePrefix, setTablePrefix] = useState<string>('');
  const styles = useStyles();

  const numberError = numberOfLocations > 50;
  const isConfirmDisabled = numberError || !numberOfLocations || !startingNumber;

  const parseNumber = (value: string): number => {
    return parseInt(value.replace(/\D/g, '')) || 0;
  };

  return (
    <Modal open={true} titleTextId={'Add_tables'}>
      <ModalContent>
        <GridContainer direction="column">
          <Grid item className={styles.gridItem}>
            <Typography variant="caption" className={styles.subTitle}>
              {translate('Add_tables_description')}
            </Typography>
          </Grid>

          <Grid item className={clsx(styles.textFields, styles.gridItem)}>
            <TextField
              variant="standard"
              fullWidth
              id="location-number-input"
              label={translate('Number_of_tables')}
              value={numberOfLocations}
              error={numberError}
              helperText={numberError && translate('Create_section_error')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNumberOfLocations(parseNumber(event.target.value));
              }}
            />
          </Grid>
          {isTablePrefixEnabled && (
            <>
              <Grid item className={clsx(styles.textFields, styles.gridItem)}>
                <TextField
                  variant="standard"
                  fullWidth
                  id="location-starting-number-input"
                  label={translate('Location_starting_number_title')}
                  helperText={translate('Location_starting_number_description')}
                  value={startingNumber}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setStartingNumber(parseNumber(event.target.value))
                  }
                />
              </Grid>
              <Grid item className={clsx(styles.textFields, styles.gridItem)}>
                <TextField
                  variant="standard"
                  fullWidth
                  id="location-prefix-input"
                  placeholder={translate('Location_prefix_placeholder') as string}
                  InputLabelProps={{ shrink: true }}
                  label={translate('Location_prefix_title')}
                  helperText={translate('Location_prefix_description')}
                  value={tablePrefix}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setTablePrefix(event.target.value);
                  }}
                />
              </Grid>
            </>
          )}
        </GridContainer>
      </ModalContent>
      <ModalActions>
        <ModalButtons
          leftButtonTranslationId="Cancel"
          leftButtonAction={onClose}
          rightButtonTranslationId="Add"
          rightButtonAction={() => onConfirm(numberOfLocations, startingNumber, tablePrefix)}
          fdKey={'create-location-modal-buttons'}
          rightDisabled={isConfirmDisabled}
        />
      </ModalActions>
    </Modal>
  );
};

export default CreateLocationModal;
